// Dominio de atribución de origen. Clasifica leads usando source +
// la evidencia del webhook persistida en leads.source_metadata.
//
// Jerarquía que ve el usuario:
//   Canal (Meta Ads / Landing / WhatsApp directo / Manual)
//     └─ Red (Instagram / Facebook) ............ solo Meta
//         └─ Campaña (ad_title del anuncio real)
//     └─ Tipo de entrada ........................ solo WhatsApp
//     └─ Detalle (cita/visita/asesor/nivel) ..... solo Landing

import { normalizeStatus } from "@/lib/supabase"
import { prettifySourceDetail } from "@/components/kanban/lead-source-badge"

/** Canal de primer nivel (Meta unificado: Instagram+Facebook+sin red). */
export function getTopChannel(lead) {
  const src = String(lead?.source || "").toLowerCase()
  if (src === "meta_ads" || src === "facebook") return "meta_ads"
  if (src === "whatsapp") return "whatsapp_directo"
  return src || "sin_fuente"
}

/** Red social del anuncio Meta: instagram | facebook | null (sin identificar). */
export function getMetaNetwork(lead) {
  const app = String(lead?.source_metadata?.app || "").toLowerCase()
  if (app === "instagram" || app === "facebook") return app
  return null
}

const titleCase = (s) =>
  String(s)
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
    .replace(/(^|\s)\p{L}/gu, (c) => c.toUpperCase())

/** Nombre de campaña/anuncio real, o el detalle legible como fallback. */
export function getCampaignName(lead) {
  const title = lead?.source_metadata?.ad_title
  if (title) return titleCase(title)
  const detail = prettifySourceDetail(lead?.source_detail)
  return detail || "Anuncio sin identificar"
}

/** Cómo entró un lead de WhatsApp directo (entry_source del webhook). */
export const WA_ENTRY_LABELS = {
  directo: "Recomendación / contacto directo",
  page_cta: "Página de Facebook (botón WhatsApp)",
  biz_profile: "Perfil de WhatsApp Business",
  global_search_new_chat: "Buscaron el número en WhatsApp",
  phone_number_hyperlink: "Tocaron un número publicado",
}

export function getWaEntry(lead) {
  const entry = lead?.source_metadata?.entry_source
  if (entry && WA_ENTRY_LABELS[entry]) return entry
  if (entry) return "otro"
  return "directo"
}

/** Detalle de landing legible (Agendar cita, Campus GDL, por nivel...). */
export function getLandingDetail(lead) {
  return prettifySourceDetail(lead?.source_detail) || "Información general"
}

/** Buckets de etapa para distribución compacta. */
export const STAGE_BUCKETS = [
  { key: "nuevo", label: "Primer contacto", color: "#38bdf8", states: ["nuevo"] },
  { key: "proceso", label: "Proceso", color: "#818cf8", states: ["contactado", "cita_solicitada"] },
  { key: "cita", label: "Cita", color: "#fbbf24", states: ["cita"] },
  { key: "examen", label: "Examen / Resultado", color: "#34d399", states: ["interesado", "entrega_resultado"] },
  { key: "pago", label: "Pendiente de pago", color: "#fb923c", states: ["pendiente_pago"] },
  { key: "inscrito", label: "Inscrito", color: "#10b981", states: ["inscrito"] },
  { key: "archivado", label: "Archivado", color: "#cbd5e1", states: ["perdido", "spam", "inactivo"] },
]

const STATE_TO_BUCKET = {}
for (const b of STAGE_BUCKETS) for (const s of b.states) STATE_TO_BUCKET[s] = b.key

export function getStageBucket(lead) {
  return STATE_TO_BUCKET[normalizeStatus(lead?.estado_lead)] || "proceso"
}

/** Acumula conteos por bucket para una lista de leads. */
export function countStages(leadList) {
  const counts = Object.fromEntries(STAGE_BUCKETS.map((b) => [b.key, 0]))
  for (const l of leadList) counts[getStageBucket(l)]++
  return counts
}
